import { call, put, select } from "redux-saga/effects";

import {
	areContentPayloadsEqual,
	buildContentCacheKey,
	contentCacheRepository,
	getContentPlatformScope,
	sanitizeContentPayload,
} from "../../services/contentCacheRepository";

const selectUserScope = (state) => String(state?.Auth?.user?.id || "guest");

export function* runCachedContentRequest({
	resource,
	params = {},
	requestKey,
	fetchRequest,
	cacheSuccessType,
	networkSuccessType,
	unchangedType,
	errorType,
	transformPayload = sanitizeContentPayload,
	onNetworkResponse,
	validateResponse,
}) {
	const userScope = yield select(selectUserScope);
	const platformScope = getContentPlatformScope();
	const cacheKey = buildContentCacheKey({ resource, params, userScope, platformScope });
	const cachedEntry = yield call([contentCacheRepository, contentCacheRepository.get], cacheKey);
	const hasCachedData = Boolean(cachedEntry?.payload);

	if (hasCachedData) {
		yield put({
			type: cacheSuccessType,
			payload: cachedEntry.payload,
			requestKey,
			cacheKey,
			source: "cache",
			refreshing: true,
			userScope,
		});
	}

	try {
		const response = yield call(
			[contentCacheRepository, contentCacheRepository.runDeduplicated],
			cacheKey,
			fetchRequest
		);
		if (response?.status !== 200) {
			throw new Error(`Unexpected content response status: ${response?.status}`);
		}
		if (validateResponse) {
			yield call(validateResponse, response);
		}

		if (onNetworkResponse) {
			yield call(onNetworkResponse, response);
		}

		const freshPayload = transformPayload(response?.data);
		const changed = !hasCachedData || !areContentPayloadsEqual(cachedEntry.payload, freshPayload);
		const responseUserScope = response?.data?.user?.id
			? String(response.data.user.id)
			: userScope;
		const networkCacheKey = buildContentCacheKey({
			resource,
			params,
			userScope: responseUserScope,
			platformScope,
		});
		if (networkCacheKey !== cacheKey) {
			yield call([contentCacheRepository, contentCacheRepository.remove], cacheKey);
		}
		yield call([contentCacheRepository, contentCacheRepository.set], {
			key: networkCacheKey,
			resource,
			params,
			userScope: responseUserScope,
			platformScope,
			payload: freshPayload,
		});

		if (changed || !unchangedType) {
			yield put({
				type: networkSuccessType,
				payload: freshPayload,
				requestKey,
				cacheKey: networkCacheKey,
				source: "network",
				refreshing: false,
				userScope: responseUserScope,
			});
		} else {
			yield put({
				type: unchangedType,
				requestKey,
				cacheKey: networkCacheKey,
				source: "network",
				refreshing: false,
				userScope: responseUserScope,
			});
		}

		return { response, payload: freshPayload, changed, hasCachedData, userScope: responseUserScope };
	} catch (error) {
		if (hasCachedData) {
			console.warn(`[ContentCache] Background refresh failed for ${resource}; cached content remains visible.`, error);
		}
		yield put({
			type: errorType,
			requestKey,
			cacheKey,
			error,
			hadCachedData: hasCachedData,
			userScope,
		});
		return { error, hasCachedData, userScope };
	}
}
